import { SrSalesOrderType } from '@plato/data/starrocks/types';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DateText } from '../../../components/common/date-time-text.tsx';
import { DataText } from '../../../components/typography/data-text.tsx';
import { QuantityText } from '../../../components/typography/quantity-text.tsx';
import { TooltipInfo } from '../../../components/ui/tooltip.tsx';
import { toCurrencyStringOrEmpty } from '../../../lib/currency-helpers.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { GetManyQuotesOrdersListContaingProductListItem } from '../../../server/types/infer-trpc-types.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useCustomerMode } from '../../customer-mode/hooks/use-customer-mode.tsx';
import { QuoteLink } from '../../quote-order-detail/components/quote-order-link.tsx';
import { OrderStatusBadge } from '../../statuses/components/order-status-badge.tsx';
import { MarginPercentage } from '../components/margin-percentage.tsx';

export const useQuoteColumnsForProductDetail = () => {
    const { t, locale } = useLocale();
    const { customerViewToggled } = useCustomerMode();
    const { canReadGrossMargin } = useAppConfig();

    const quoteColumnsForProductDetail: ColumnDef<GetManyQuotesOrdersListContaingProductListItem>[] = useMemo(
        () => [
            {
                accessorKey: 'customer_name',
                header: t('table_header__customer', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row, cell }) => (
                    <Link
                        className="font-medium hover:underline"
                        to={`/customers/${cell.row.original.customer_id}`}
                    >
                        <span className="text-blue-700">{row.original.customer_id}</span>
                        {` ${cell.renderValue() /* eslint-disable-line @typescript-eslint/restrict-template-expressions -- legacy code */}`}
                    </Link>
                ),
                size: 250,
            },
            {
                accessorKey: 'order_id',
                header: t('table_header__quote_id', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <QuoteLink transactionId={row.original.order_id} />,
            },
            {
                accessorKey: 'status',
                header: t('table_header__status', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <OrderStatusBadge status={row.original.status} type={SrSalesOrderType.QUOTE} />
                ),
            },
            {
                accessorKey: 'entry_date',
                header: t('table_header__quote_created', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <DateText date={row.original.entry_date} />,
            },
            {
                accessorKey: 'item_quantity_in_order',
                header: t('table_header__quantity', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <QuantityText
                        quantity={row.original.item_quantity_in_order}
                        unit={row.original.quantity_unit_sales}
                    />
                ),
            },

            {
                accessorKey: 'product_unit_price',
                header: t('table_header__price', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => toCurrencyStringOrEmpty(row.original.product_unit_price, locale),
            },
            {
                accessorKey: 'product_total_in_order',
                header: () => (
                    <div className="flex items-center gap-tight">
                        {t('table_header__total', { ns: 'quotes-orders-list' })}
                        <TooltipInfo>
                            {t('table_header__total_tooltip', { ns: 'product-details' })}
                        </TooltipInfo>
                    </div>
                ),
                cell: ({ row }) => toCurrencyStringOrEmpty(row.original.product_total_in_order, locale),
            },
            ...(customerViewToggled || !canReadGrossMargin
                ? []
                : ([
                      {
                          accessorKey: 'product_gross_margin_in_order',
                          header: t('table_header__margin', {
                              ns: 'quotes-orders-list',
                          }),
                          cell: ({ row }) =>
                              toCurrencyStringOrEmpty(row.original.product_gross_margin_in_order, locale),
                      },
                      {
                          accessorKey: 'product_gross_margin_percent',
                          header: t('table_header__margin_percent', {
                              ns: 'quotes-orders-list',
                          }),
                          cell: ({ row }) => (
                              <DataText>
                                  <MarginPercentage
                                      margin={row.original.product_gross_margin_in_order}
                                      revenue={row.original.product_total_in_order}
                                  />
                              </DataText>
                          ),
                      },
                  ] as ColumnDef<GetManyQuotesOrdersListContaingProductListItem>[])),
        ],
        [t, canReadGrossMargin, customerViewToggled, locale],
    );

    return quoteColumnsForProductDetail;
};
